import { useState, useCallback, useEffect } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ACCESS_WORD } from "@/lib/accessKey";
import { formatApiErrorDetail } from "@/lib/api";
import { Delete, HardHat, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PIN_LENGTH = 4;
// Module-scoped guard: survives React StrictMode's double effect-invoke AND
// double-mount, so a 4-digit entry fires exactly ONE login request.
let pinSubmitInFlight = false;

export default function DriverPinLogin() {
  const { user, loginWithPin } = useAuth();
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [shake, setShake] = useState(false);
  const nav = useNavigate();

  const submit = useCallback(async (code) => {
    setBusy(true);
    setErr("");
    try {
      const u = await loginWithPin(code);
      toast.success(`Welcome, ${u.name}`);
      nav("/driver", { replace: true });
    } catch (e) {
      const detail = e.response?.data?.detail;
      setErr(detail != null ? formatApiErrorDetail(detail) : "Cannot reach the server. Try again.");
      setPin("");
      setShake(true);
      setTimeout(() => setShake(false), 500);
      pinSubmitInFlight = false;   // allow a retry only after a FAILED attempt
    } finally {
      setBusy(false);
    }
  }, [loginWithPin, nav]);

  // Auto-submit once 4 digits are in. The module-level flag guarantees a single
  // request even though StrictMode invokes effects twice in dev.
  useEffect(() => {
    if (pin.length === PIN_LENGTH && !pinSubmitInFlight) {
      pinSubmitInFlight = true;
      submit(pin);
    }
  }, [pin, submit]);

  const press = useCallback((d) => {
    if (busy) return;
    setErr("");
    setPin((prev) => (prev.length >= PIN_LENGTH ? prev : prev + d));
  }, [busy]);

  const back = useCallback(() => { if (!busy) setPin((p) => p.slice(0, -1)); }, [busy]);

  // Physical / bluetooth keypad support
  useEffect(() => {
    const onKey = (e) => {
      if (e.key >= "0" && e.key <= "9") press(e.key);
      else if (e.key === "Backspace") back();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [press, back]);

  // Already signed in → straight to the driver app.
  if (user && user !== false && user !== "trial_expired") {
    return <Navigate to={user.role === "driver" ? "/driver" : "/"} replace />;
  }

  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-neutral-950 text-white px-6 py-10 select-none" data-testid="driver-pin-login">
      <div className="flex flex-col items-center mb-8">
        <div className="w-16 h-16 rounded-2xl bg-primary-orange flex items-center justify-center shadow-xl shadow-orange-900/40 mb-4">
          <HardHat className="w-8 h-8 text-black" />
        </div>
        <div className="heading text-2xl font-black uppercase tracking-tight !text-white">Driver Sign In</div>
        <div className="mono text-[11px] uppercase tracking-[0.3em] text-neutral-500 mt-2">Enter your 4-digit PIN</div>
      </div>

      {/* PIN dots */}
      <div className={`flex items-center gap-4 mb-3 ${shake ? "animate-[shake_0.4s]" : ""}`} data-testid="pin-dots">
        {Array.from({ length: PIN_LENGTH }).map((_, i) => (
          <div
            key={i}
            data-testid={`pin-dot-${i}`}
            className={`w-4 h-4 rounded-full border-2 transition-colors ${
              i < pin.length ? "bg-primary-orange border-primary-orange" : "border-neutral-600"
            }`}
          />
        ))}
      </div>

      <div className="h-6 mb-4">
        {busy ? (
          <div className="flex items-center gap-2 text-neutral-400 mono text-xs uppercase tracking-widest" data-testid="pin-checking">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking…
          </div>
        ) : err ? (
          <div className="text-red-400 mono text-xs uppercase tracking-widest" data-testid="pin-error">{err}</div>
        ) : null}
      </div>

      {/* Number pad */}
      <div className="grid grid-cols-3 gap-4" data-testid="pin-pad">
        {keys.map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => press(k)}
            disabled={busy}
            data-testid={`pin-key-${k}`}
            className="w-20 h-20 rounded-full bg-neutral-800 active:bg-primary-orange active:text-black text-3xl font-bold flex items-center justify-center transition-colors disabled:opacity-50"
          >
            {k}
          </button>
        ))}
        <div className="w-20 h-20" />
        <button
          type="button"
          onClick={() => press("0")}
          disabled={busy}
          data-testid="pin-key-0"
          className="w-20 h-20 rounded-full bg-neutral-800 active:bg-primary-orange active:text-black text-3xl font-bold flex items-center justify-center transition-colors disabled:opacity-50"
        >
          0
        </button>
        <button
          type="button"
          onClick={back}
          disabled={busy || pin.length === 0}
          data-testid="pin-key-back"
          className="w-20 h-20 rounded-full flex items-center justify-center text-neutral-400 active:text-white transition-colors disabled:opacity-30"
        >
          <Delete className="w-7 h-7" />
        </button>
      </div>

      <a href={`/login?key=${ACCESS_WORD}`} data-testid="driver-pin-to-staff-login" className="mt-10 mono text-[11px] uppercase tracking-widest text-neutral-500 hover:text-primary-orange">
        Staff login →
      </a>
    </div>
  );
}
